import { useState } from 'react';
import { Link } from 'react-router-dom';

type Category = 'All' | 'Stretch Tents' | 'Functions & Festivals';
type SortOrder = 'Newest First' | 'Oldest First';

interface BlogPost {
  id: number;
  title: string;
  category: 'Stretch Tents' | 'Functions & Festivals';
  date: string;
  dateValue: number;
  readTime: string;
  image: string;
  excerpt: string;
  slug: string;
}

const allPosts: BlogPost[] = [
  {
    id: 1,
    title: 'Case Study: How Pop Pro Tents® Transformed the Experience at Lightning in a Bottle Festival',
    category: 'Stretch Tents',
    date: 'Mar 5, 2026',
    dateValue: 20260305,
    readTime: '7 mins read',
    image: '/assets/img/128b6f60dbc3.webp',
    excerpt: 'Discover how our custom stretch tent installation elevated the guest experience at one of the most iconic outdoor festivals in the US.',
    slug: 'lightning-in-a-bottle-festival',
  },
  {
    id: 2,
    title: "Enhance Your Festival's Appeal: Strategic Uses of Stretch Tents for Better Guest Experience",
    category: 'Stretch Tents',
    date: 'Feb 18, 2026',
    dateValue: 20260218,
    readTime: '5 mins read',
    image: '/assets/img/fe8d525f3474.webp',
    excerpt: 'Learn how strategically placed stretch tents can improve crowd flow, shelter, and the overall festival atmosphere.',
    slug: 'enhance-festival-appeal-stretch-tents',
  },
  {
    id: 3,
    title: 'Stretch Tent Rental Guide: Tips and Considerations for Your Event',
    category: 'Stretch Tents',
    date: 'Jan 30, 2026',
    dateValue: 20260130,
    readTime: '5 mins read',
    image: '/assets/img/ddf123f33140.webp',
    excerpt: 'Everything you need to know before renting a stretch tent, from sizing and anchoring to weather considerations and permits.',
    slug: 'stretch-tent-rentals-guide-tips-considerations',
  },
  {
    id: 4,
    title: 'Luxury Bedouin Tents for Exclusive Events: Benefits and Features',
    category: 'Functions & Festivals',
    date: 'Jan 12, 2026',
    dateValue: 20260112,
    readTime: '6 mins read',
    image: '/assets/img/699f4309f127.webp',
    excerpt: 'Explore the elegance and practicality of Bedouin-style stretch tents for upscale private events and exclusive celebrations.',
    slug: 'luxury-bedouin-tents-exclusive-events',
  },
  {
    id: 5,
    title: 'What Stretch Tent Size Is Suitable For My Outdoor Event?',
    category: 'Stretch Tents',
    date: 'Dec 28, 2025',
    dateValue: 20251228,
    readTime: '5 mins read',
    image: '/assets/img/c2288a635df5.webp',
    excerpt: 'A practical guide to choosing the right stretch tent size based on your guest count, venue layout, and event type.',
    slug: 'suitable-stretch-tent-sizes-for-your-outdoor-event',
  },
  {
    id: 6,
    title: 'A Behind-the-Scenes Look at Pop Pro Tents: Leaders in Stretch Tent Rentals',
    category: 'Stretch Tents',
    date: 'Dec 10, 2025',
    dateValue: 20251210,
    readTime: '5 mins read',
    image: '/assets/img/fe6b7f94f31f.webp',
    excerpt: "Take a peek behind the curtain at how Pop Pro Tents designs, manufactures, and delivers world-class stretch tents across the country.",
    slug: 'behind-the-scenes-pop-pro-tents',
  },
  {
    id: 7,
    title: 'Choosing the Perfect Festival Stretch Tent for Your Outdoor Music Festival',
    category: 'Stretch Tents',
    date: 'Sep 12, 2024',
    dateValue: 20240912,
    readTime: '5 mins read',
    image: '/assets/img/862f4e2cba8e.webp',
    excerpt: 'From size and capacity to customisation and safety, everything you need to know to choose the right festival stretch tent for your outdoor event.',
    slug: 'festival-stretch-tent-outdoor-music-festival',
  },
  {
    id: 8,
    title: '10 Reasons To Choose A Stretch Tent For Your Next Event',
    category: 'Stretch Tents',
    date: 'Sep 12, 2024',
    dateValue: 20240912,
    readTime: '4 mins read',
    image: '/assets/img/3a326e298138.webp',
    excerpt: 'Here are ten compelling reasons why a stretch tent from Pop Pro Tents should be at the top of your list for your next event.',
    slug: '10-reasons-choose-stretch-tent-next-event',
  },
  {
    id: 9,
    title: 'Why Our Stretch Tent Fabrics Outperform Other Fabrics',
    category: 'Stretch Tents',
    date: 'Oct 30, 2023',
    dateValue: 20231030,
    readTime: '3 mins read',
    image: '/assets/img/37ff50868588.webp',
    excerpt: 'From durability and waterproofing to breathability and lightweight design, discover what makes our stretch tent fabrics superior to the rest.',
    slug: 'our-stretch-tent-fabrics-outperform-other-fabrics',
  },
];

const categories: Category[] = ['All', 'Stretch Tents', 'Functions & Festivals'];
const sortOptions: SortOrder[] = ['Newest First', 'Oldest First'];

const POSTS_PER_PAGE = 6;

export default function BlogContent() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [sortOrder, setSortOrder] = useState<SortOrder>('Newest First');
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  const filtered = allPosts
    .filter((p) => activeCategory === 'All' || p.category === activeCategory)
    .sort((a, b) =>
      sortOrder === 'Newest First' ? b.dateValue - a.dateValue : a.dateValue - b.dateValue
    );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <section className="bg-white">
      {/* Filter bar */}
      <div className="sticky top-[96px] z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Category tabs */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setVisibleCount(POSTS_PER_PAGE); }}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-teal-400 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <div className="relative">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              className="appearance-none bg-gray-100 text-gray-700 text-sm font-medium pl-4 pr-10 py-2 rounded-lg border-none outline-none cursor-pointer"
            >
              {sortOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"></i>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {visible.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-base">
            No posts found in this category.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visible.map((post) => (
              <article
                key={post.id}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-transform duration-300"
              >
                {/* Featured image */}
                <div className="relative h-52 overflow-hidden bg-gray-100">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col gap-3">
                  {/* Category tag */}
                  <span className="inline-block w-fit px-3 py-1 bg-teal-50 text-teal-600 text-xs font-bold rounded-full">
                    {post.category}
                  </span>

                  {/* Title */}
                  <h2 className="font-bold text-gray-900 text-base leading-snug line-clamp-3">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {post.date}
                    </span>
                    <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>

                  {/* Read More */}
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1.5 text-teal-500 hover:text-teal-600 font-semibold text-sm mt-1 group/btn whitespace-nowrap"
                  >
                    <span>Read More</span>
                    <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-14 text-center">
          {hasMore ? (
            <button
              onClick={() => setVisibleCount((v) => v + POSTS_PER_PAGE)}
              className="bg-teal-400 hover:bg-teal-500 text-white px-10 py-3.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              Load More
            </button>
          ) : (
            <p className="text-gray-400 text-sm">You&apos;ve reached the end. More posts coming soon!</p>
          )}
        </div>
      </div>
    </section>
  );
}
